import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function ManageBookingEngine() {

  const [data,       setData]       = useState([]);
  const [params,     setParams]     = useState([]);   // tbl_bkngengine_para dropdown
  const [page,       setPage]       = useState(1);
  const [limit,      setLimit]      = useState(null); // from tbl_settings
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [editId,     setEditId]     = useState(null); // bkngeng_id being edited

  const [form, setForm] = useState({
    eng_id:      '',
    engpar_value: ''
  });

  // eng_id=1 is the special Horizontal/Vertical select (mirrors PHP enginetype())
  const isEngineType = form.eng_id == 1;

  /* ── Step 1: Load page limit from tbl_settings ── */
  useEffect(() => {
    API.get('/settings/page-limit')
      .then(res => setLimit(Number(res.data.limit) || 10))
      .catch(()  => setLimit(10));
  }, []);

  /* ── Step 2: Load engine parameters dropdown ── */
  useEffect(() => {
    API.get('/booking-engine/parameters')
      .then(res => setParams(res.data || []))
      .catch(() => setParams([]));
  }, []);

  /* ── Step 3: Fetch table data after limit ready ── */
  useEffect(() => {
    if (limit === null) return;
    fetchData();
  }, [page, limit]);

  const fetchData = () => {
    setLoading(true);
    API.get(`/booking-engine?page=${page}&limit=${limit}`)
      .then(res => {
        setData(res.data?.data   || []);
        setTotal(res.data?.total || 0);
      })
      .catch(err => {
        console.error('FETCH ERROR:', err.response?.data || err.message);
        toast.error('Failed to load data');
        setData([]);
      })
      .finally(() => setLoading(false));
  };

  /* ── Submit (Add / Update) ── */
  const handleSubmit = async () => {
    if (!form.eng_id) {
      toast.warning('Please select a Parameter');
      return;
    }
    if (!form.engpar_value) {
      toast.warning('Please enter a Value');
      return;
    }

    try {
      if (editId) {
        await API.put(`/booking-engine/${editId}`, form);
        toast.success('Updated Successfully');
      } else {
        await API.post('/booking-engine', form);
        toast.success('Added Successfully');
      }
      setForm({ eng_id: '', engpar_value: '' });
      setEditId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to save, try again');
    }
  };

  /* ── Start Edit ── */
  const handleEdit = (row) => {
    setForm({
      eng_id:       row.eng_id,
      // store raw value for select/input to work correctly
      engpar_value: row.eng_id == 1
        ? (row.display_value === 'Horizontal' ? '1' : '2')
        : row.engpar_value
    });
    setEditId(row.bkngeng_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── Cancel ── */
  const handleCancel = () => {
    setForm({ eng_id: '', engpar_value: '' });
    setEditId(null);
  };

  /* ── When eng_id changes, reset value ── */
  const handleParamChange = (e) => {
    const val = e.target.value;
    setForm({
      eng_id: val,
      // default to '1' (Horizontal) when engine type param selected
      engpar_value: val == 1 ? '1' : ''
    });
  };

  const totalPages = limit ? Math.ceil(total / limit) : 1;

  if (limit === null) {
    return (
      <Layout activePage="BookingEngine">
        <div style={S.emptyCell}>Loading settings...</div>
      </Layout>
    );
  }

  return (
    <Layout activePage="BookingEngine">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── TOP BAR ── */}
      <div style={S.topbar}>
        <div>
          <h2 style={S.title}>Booking Engine</h2>
          <p style={S.subtitle}>Manage booking engine parameters</p>
        </div>
      </div>

      {/* ── FORM CARD ── */}
      <div style={S.formCard}>
        <div style={S.formTitle}>
          {editId ? '✏️ Edit Value' : '➕ Add Value'}
        </div>

        <div style={S.formGrid}>

          {/* Engine Parameter dropdown */}
          <div style={S.fg}>
            <label style={S.label}>
              Engine Parameter <span style={S.req}>*</span>
            </label>
            <select
              value={form.eng_id}
              onChange={handleParamChange}
              style={S.input}
              disabled={!!editId} // can't change parameter during edit (PHP behaviour)
            >
              <option value="">---Select---</option>
              {params.map(p => (
                <option key={p.eng_id} value={p.eng_id}>
                  {p.eng_parameter}
                </option>
              ))}
            </select>
          </div>

          {/* Value — text input OR Horizontal/Vertical select (mirrors PHP enginetype()) */}
          <div style={S.fg}>
            <label style={S.label}>
              Value <span style={S.req}>*</span>
            </label>

            {isEngineType ? (
              <select
                value={form.engpar_value}
                onChange={e => setForm({ ...form, engpar_value: e.target.value })}
                style={S.input}
              >
                <option value="1">Horizontal</option>
                <option value="2">Vertical</option>
              </select>
            ) : (
              <input
                placeholder="Enter value..."
                value={form.engpar_value}
                maxLength={250}
                onChange={e => setForm({ ...form, engpar_value: e.target.value })}
                style={S.input}
              />
            )}
          </div>

        </div>

        <div style={S.formActions}>
          {editId && (
            <button style={S.cancelBtn} onClick={handleCancel}>Clear</button>
          )}
          <button style={S.submitBtn} onClick={handleSubmit}>
            {editId ? '💾 Update' : '✚ Submit'}
          </button>
        </div>
      </div>

      {/* ── TABLE CARD ── */}
      <div style={S.card}>
        {loading ? (
          <div style={S.emptyCell}>Loading...</div>
        ) : data.length === 0 ? (
          <div style={{ ...S.emptyCell, color: 'red' }}>-- No records Available --</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Parameter</th>
                <th style={S.th}>Value</th>
                <th style={S.th}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={row.bkngeng_id}>
                  <td style={S.td}>
                    <span style={S.serial}>{(page - 1) * limit + i + 1}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.groupChip}>{row.eng_parameter}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.nameChip}>{row.display_value}</span>
                  </td>
                  <td style={S.td}>
                    <button
                      style={S.editBtn}
                      onClick={() => handleEdit(row)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* ── PAGINATION ── */}
        <div style={S.pagination}>
          <button
            style={{ ...S.pageBtn, opacity: page === 1 ? 0.5 : 1 }}
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            ◀ Prev
          </button>

          <span style={S.pageInfo}>
            Page {page} of {totalPages || 1}
            <span style={{ color: '#aaa', fontWeight: 400, marginLeft: 8 }}>
              ({total} total)
            </span>
          </span>

          <button
            style={{ ...S.pageBtn, opacity: page >= totalPages ? 0.5 : 1 }}
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next ▶
          </button>
        </div>
      </div>
    </Layout>
  );
}


/* ─── Styles ─── */
const S = {
  topbar:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:    { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },

  formCard:    { background: '#eef0f5', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', padding: '20px 24px', marginBottom: 20 },
  formTitle:   { fontSize: 14, fontWeight: 700, color: '#444', marginBottom: 16 },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  fg:          { display: 'flex', flexDirection: 'column', gap: 5 },
  label:       { fontSize: 12, fontWeight: 600, color: '#555' },
  req:         { color: '#ff6b6b' },
  input: {
    padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box', background: '#fff'
  },
  submitBtn: {
    padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit'
  },
  cancelBtn: {
    padding: '9px 20px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit'
  },

  card:     { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:    { width: '100%', borderCollapse: 'collapse' },
  theadRow: { background: '#fafafa' },
  th: {
    padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'center', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  td:        { padding: '12px 14px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8', textAlign: 'center' },
  emptyCell: { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:    { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  nameChip:  { background: '#f0fff6', color: '#1a9e5c', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  groupChip: { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },

  editBtn: {
    background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },

  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0'
  },
  pageBtn: {
    padding: '6px 14px', border: 'none', borderRadius: 8,
    background: '#6C63FF', color: '#fff', cursor: 'pointer',
    fontFamily: 'inherit', fontWeight: 600
  },
  pageInfo: { fontSize: 14, fontWeight: 600 },
};